import type { ComponentProps, ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'motion/react';

import config from '@/config';
import { NavbarIconLogo } from '@shared/ui/navbar-icon-logo';
import { useGoBack } from '@shared/lib/hooks';
import { useT } from '@shared/i18n';

import styles from './topbar.module.css';

export function Topbar(props: TopbarProps) {
	const { left, center, right, className, motionProps } = props;
	const navClassName = className
		? `${styles['topbar']} ${className}`
		: styles['topbar'];

	const content = (
		<>
			<div className={styles['left']}>{left ?? <BackButton />}</div>
			<div className={styles['center']}>
				{center ?? <NavbarIconLogo />}
			</div>
			<div className={styles['right']}>{right}</div>
		</>
	);

	// `motionProps` opts a caller into a `motion.nav` instead of a plain one
	// -- needed by the issue reader's topbar (visibility fade on scroll),
	// unused (and so zero behavior change) by every other caller.
	if (motionProps) {
		return (
			<motion.nav className={navClassName} {...motionProps}>
				{content}
			</motion.nav>
		);
	}

	return <nav className={navClassName}>{content}</nav>;
}

export function BackButton(props: { fallback?: string }) {
	const t = useT();
	if (config.app.template.type === 'basic') {
		return null;
	}
	const goBack = useGoBack(props.fallback);
	return (
		<button id="back-button" type="button" title={t('common.back')} onClick={goBack}>
			<ArrowLeft id="back-btn" color="var(--topbar-buttons-stroke)" />
		</button>
	);
}

export interface TopbarProps {
	left?: ReactNode;
	center?: ReactNode;
	right?: ReactNode;
	className?: string;
	motionProps?: Omit<
		ComponentProps<typeof motion.nav>,
		'className' | 'children'
	>;
}

/**
 * The shape a page/template passes down to a reader widget (`Responsive`,
 * `ReplicaView`, `SingleView`, `DoubleView`) that builds its own `left`/
 * `right` from several buttons and wants to append tenant-specific ones
 * after them, rather than owning the whole slot.
 */
export interface TopbarExtraButtons {
	left?: ReactNode;
	right?: ReactNode;
}
